/* eslint-disable @typescript-eslint/no-unused-vars */
// Location: src/components/admin/URLsSection.tsx
// Purpose: URLs section for pod management
// Scope: Shows URLs for a pod and allows for URL management

import React from 'react';
import { Button } from '@/components/ui/button';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
	getPodModeCapability,
	getPodPlatformLabel,
	Pod,
} from '@/types/pods';
import { motion, AnimatePresence } from 'framer-motion';
import { Id } from '@/convex/_generated/dataModel';

interface URL {
	_id: Id<'urls'>;
	url: string;
	username?: string;
	isActive: boolean;
	isArchived: boolean;
}

interface URLsSectionProps {
	pod: Pod;
	urls: URL[];
	handleToggleUrlActive: (urlId: Id<'urls'>) => void;
	setUrlToArchive: (urlId: Id<'urls'>) => void;
	urlButtonDisabled: Record<string, boolean>;
	setShowAddUrlDialog: (show: boolean) => void;
	hasProfile: boolean;
	expandedGroups: Record<string, boolean>;
	setExpandedGroups: React.Dispatch<
		React.SetStateAction<Record<string, boolean>>
	>;
}

export function URLsSection({
	pod,
	urls,
	handleToggleUrlActive,
	setUrlToArchive,
	urlButtonDisabled,
	setShowAddUrlDialog,
	hasProfile,
	expandedGroups,
	setExpandedGroups,
}: URLsSectionProps) {
	const hasUrls = urls?.filter((url) => !url.isArchived).length > 0;
	const podMode = pod.mode === 'broadcast' ? 'broadcast' : 'swarm';
	const modeCapability = getPodModeCapability(pod.platform, podMode);
	const canAddUrls = hasProfile && modeCapability.enabled;

	return (
		<div>
			<div className='flex items-center justify-between mb-4'>
				<div className='space-y-1'>
					<div className='flex items-center gap-2'>
						<motion.button
							onClick={() =>
								setExpandedGroups((prev) => ({
									...prev,
									urls: !prev.urls,
								}))
							}
							className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
							animate={{ rotate: expandedGroups.urls ? 180 : 0 }}
							transition={{ duration: 0.2 }}
						>
							{expandedGroups.urls ? '🔽' : '▶️'}
						</motion.button>
						<h3 className='text-3xl font-bold'>Pod URLs</h3>
					</div>
					<p className='text-sm text-gray-500'>
						Manage the URLs that this pod will engage with
					</p>
				</div>
			</div>
			<AnimatePresence>
				{expandedGroups.urls && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
						className='overflow-hidden'
					>
						<div className='flex items-center justify-end mb-6'>
							<div className='relative group'>
								<Button
									size='lg'
									onClick={() => setShowAddUrlDialog(true)}
									className='bg-gradient-to-b from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
									disabled={!canAddUrls}
								>
									<span className='inline-flex items-center gap-2'>
										<span className='text-xl'>➕</span>
										<span>Add New URL</span>
									</span>
								</Button>
								{!canAddUrls && (
									<div className='absolute -top-12 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap'>
										{!hasProfile
											? 'Add a social profile before adding URLs'
											: `${getPodPlatformLabel(pod.platform)} ${podMode} URL entry is disabled`}
									</div>
								)}
							</div>
						</div>
						{!modeCapability.enabled && (
							<div className='mb-6 rounded-md border border-orange-300 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
								{modeCapability.reason}
							</div>
						)}
						{hasUrls ? (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>URL</TableHead>
										<TableHead>Username</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{urls
										?.filter((url) => !url.isArchived)
										.map((url) => (
											<TableRow key={url._id.toString()}>
												<TableCell className='max-w-[300px] truncate'>
													<a
														href={url.url}
														target='_blank'
														rel='noopener noreferrer'
														className='text-blue-500 hover:text-blue-700 underline'
													>
														{url.url}
													</a>
												</TableCell>
												<TableCell>
													{url.username ||
														'Unknown User'}
												</TableCell>
												<TableCell>
													<span
														className={`${url.isActive ? 'text-green-500' : 'text-red-500'} font-semibold whitespace-nowrap`}
													>
														{url.isActive
															? '🟢 Active'
															: '🔴 Inactive'}
													</span>
												</TableCell>
												<TableCell>
													<div className='flex gap-2 min-w-[220px]'>
														<Button
															variant='outline'
															size='sm'
															onClick={() =>
																handleToggleUrlActive(
																	url._id
																)
															}
															disabled={
																urlButtonDisabled[
																	url._id.toString()
																]
															}
															className={cn(
																'whitespace-nowrap',
																url.isActive
																	? 'bg-orange-500 hover:bg-orange-600 text-white'
																	: 'bg-emerald-600 hover:bg-emerald-700 text-white'
															)}
														>
															<span className='inline-flex items-center'>
																{url.isActive
																	? '🚫 Deactivate'
																	: '✅ Activate'}
															</span>
														</Button>
														<Button
															variant='outline'
															size='sm'
															onClick={() =>
																setUrlToArchive(
																	url._id
																)
															}
															className='bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 whitespace-nowrap'
														>
															<span className='inline-flex items-center'>
																📦 Archive
															</span>
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))}
								</TableBody>
							</Table>
						) : (
							<div className='text-center text-gray-500 py-8'>
								No URLs added yet. Click the button above to add
								your first URL.
							</div>
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}
